import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { Company } from '../models/company.interface';
import { CompanyModel } from '../models/company.model';
import { keysToCamelCase, toSnakeCase } from '../utils/case-converter';
import { environment } from 'src/environments/environment';
import { Office } from '../models/office.interface';
export interface DataTablesResponse {
  draw?: number;
  recordsTotal: number;
  recordsFiltered: number;
  data: any[];
}

export interface CompanyResponse {
  data: Company[];
  total: number;
}

export interface DataTablesRequest {
  draw: number;
  start: number;
  length: number;
  search: { value: string };
  order: Array<{ column: number; dir: 'asc' | 'desc' }>;
  columns: Array<{ data: string; searchable: boolean; orderable: boolean; search: { value: string } }>;
  extraFilters: { [key: string]: any };
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {

  private companiesSubject = new BehaviorSubject<Company[]>([]);
  public companies$ = this.companiesSubject.asObservable();
  public selectedCompanyId$ = new BehaviorSubject<string | null>(null);
  private API_CORPORATIONS = `${environment.laravelBackoffice}${environment.corporations}`;



  constructor(private http: HttpClient) { }

  loadCompanies(): void {
    this.http.get<Company[]>(this.API_CORPORATIONS).subscribe({
      next: (companies) => this.companiesSubject.next(keysToCamelCase(companies)),
      error: (err) => console.error('Error cargando empresas:', err)
    });
  }
  loadAllCompanies(): Observable<Company[]> {
    return this.http.get<Company[]>(this.API_CORPORATIONS)
  }


  getAllCompanies(): Company[] {
    return this.companiesSubject.value;
  }

  getAllCompanies$(): Observable<Company[]> {
    return this.companiesSubject.asObservable();
  }


  getCompaniesList(request: DataTablesRequest): Observable<DataTablesResponse> {
    return this.companies$.pipe(
      map((companies) => {
        const recordsTotal = companies.length;
        if (request.search && request.search.value) {
          const searchValue = request.search.value.toLowerCase();
          companies = companies.filter(company =>
            company.legalName.toLowerCase().includes(searchValue)
          );
        }

        // FILTROS PERSONALIZADOS
        const filters = request.extraFilters || {};
        Object.keys(filters).forEach(key => {
          const filterValue = filters[key];
          if (filterValue === null || filterValue === undefined) return;

          if (typeof filterValue === 'string') {
            const trimmed = filterValue.trim();
            if (trimmed === '') return;

            companies = companies.filter(item => {
              const itemValue = (item as any)[key];
              if (typeof itemValue === 'object' && itemValue !== null && 'name' in itemValue) {
                return itemValue.name.toLowerCase().includes(trimmed.toLowerCase());
              }
              return itemValue?.toString().toLowerCase().includes(trimmed.toLowerCase());
            });
          }

          if (
            typeof filterValue === 'object' &&
            filterValue.year !== undefined &&
            filterValue.month !== undefined &&
            filterValue.day !== undefined
          ) {
            companies = companies.filter(item => {
              const itemDateObj = (item as any)[key];
              if (!itemDateObj) return false;

              const itemDate = new Date(itemDateObj.year, itemDateObj.month - 1, itemDateObj.day);
              const filterDate = new Date(filterValue.year, filterValue.month - 1, filterValue.day);

              return itemDate.toDateString() === filterDate.toDateString();
            });
          }

        });

        if (request.order && request.order.length > 0) {
          const orderCol = request.order[0].column;
          const orderDir = request.order[0].dir;

          const columnName = request.columns[orderCol]?.data || 'id';

          companies.sort((a, b) => {
            const aValue = (a as any)[columnName];
            const bValue = (b as any)[columnName];
            if (aValue < bValue) return orderDir === 'asc' ? -1 : 1;
            if (aValue > bValue) return orderDir === 'asc' ? 1 : -1;
            return 0;
          });
        }

        const recordsFiltered = companies.length;

        const start = request.start;
        const length = request.length;
        const pagedData = companies.slice(start, start + length);

        return {
          draw: request.draw,
          recordsTotal,
          recordsFiltered,
          data: pagedData,
        };
      })
    );
  }


  getCompany(id: string): Observable<Company | undefined> {
    return this.http.get<CompanyModel>(`${this.API_CORPORATIONS}/${id}`).pipe(
      map(data => keysToCamelCase(data))
    );
  }

  createCompany(company: Company): Observable<Company> {
    return this.http.post<CompanyModel>(this.API_CORPORATIONS, toSnakeCase(company)).pipe(
      tap((newCompany) => {
        const currentCompanies = this.companiesSubject.value;
        this.companiesSubject.next([...currentCompanies, keysToCamelCase(newCompany)]);
      })
    );
  }

  updateCompany(id: string, company: Company): Observable<Company> {
    return this.http.put<CompanyModel>(`${this.API_CORPORATIONS}/${id}`, toSnakeCase(company)).pipe(
      tap((updatedCompany) => {
        const currentCompanies = this.companiesSubject.value;
        const updatedList = currentCompanies.map((company) =>
          company.id === id ? updatedCompany : company
        );
        this.companiesSubject.next(keysToCamelCase(updatedList));
      })
    );
  }

  deleteCompany(id: string): Observable<Company | undefined> {
    const company = this.companiesSubject.value.find(company => company.id === id);
    if (company) {
      company.active = false;
      return this.http.put<CompanyModel>(`${this.API_CORPORATIONS}/${id}`, toSnakeCase(company)).pipe(
        tap((updatedCompany) => {
          const currentCompanies = this.companiesSubject.value;
          const updatedList = currentCompanies.map((company) =>
            company.id === id ? updatedCompany : company
          );
          this.companiesSubject.next(keysToCamelCase(updatedList));
        })
      );
    }
    return of();
  }

  createCompanyByCSV(newCompanies: Company[]): void {
    newCompanies.map(company => {
      console.log(company)
      const companyNew = new CompanyModel(company)
      console.log(companyNew)
      //this.companies.push(companyNew)
    });
  }

  setCompanyId(id: string) {
    this.selectedCompanyId$.next(id);
    localStorage.setItem('selectedCompanyId', id);
  }

  getCompanyId$() {
    return this.selectedCompanyId$.asObservable();
  }

  getCompanyId(): string | null {
    return this.selectedCompanyId$.getValue();
  }

  clearCompany() {
    this.selectedCompanyId$.next(null);
    localStorage.removeItem('selectedCompanyId');
  }

  initSelectedCompanyId(companies: Company[]) {
    const saved = localStorage.getItem('selectedCompanyId');
    this.setCompanyId(saved || '');
    return saved || '';

  }
}
